import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Anchor,
  Button,
  Divider,
  Group,
  List,
  Loader,
  PasswordInput,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconFlask, IconShieldLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { ApiError, apiFetch, errorMessage } from '@/api/client';
import { AuthShell } from '@/features/auth/AuthShell';

import { clinicUrl, type Platform, suggestAddress, trialText } from './platform';

const fields = z.object({
  clinic_name: z.string().trim().min(2, 'Obligatoire'),
  address: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9-]{1,28}[a-z0-9]$/, 'De 3 à 30 caractères : a-z, 0-9 et tirets'),
  city: z.string().trim().optional(),
  phone: z.string().trim().optional(),
  email: z.union([z.literal(''), z.string().trim().email('Adresse e-mail invalide')]).optional(),
  admin_first_name: z.string().trim().min(1, 'Obligatoire'),
  admin_last_name: z.string().trim().min(1, 'Obligatoire'),
  admin_username: z
    .string()
    .trim()
    .regex(/^[a-zA-Z0-9._-]{3,50}$/, 'De 3 à 50 caractères : lettres, chiffres, point, tiret'),
  admin_email: z.string().trim().email('Adresse e-mail invalide'),
  password: z.string().min(10, '10 caractères minimum'),
  password_confirm: z.string(),
});

const schema = fields.refine((v) => v.password === v.password_confirm, {
  path: ['password_confirm'],
  message: 'Les deux mots de passe ne correspondent pas',
});

type Values = z.infer<typeof schema>;

interface AddressCheck {
  address: string;
  domain: string;
  available: boolean;
  reason: string;
}

export interface Signup {
  id: string;
  status: 'EN_ATTENTE' | 'EN_COURS' | 'PRETE' | 'ECHEC';
  status_label: string;
  clinic_name: string;
  address: string;
  domain: string;
  url: string;
  admin_username: string;
  trial_ends_at: string | null;
  message: string;
}

const POLL_MS = 2000;

function useAddressCheck(address: string) {
  const [check, setCheck] = useState<AddressCheck | null>(null);
  useEffect(() => {
    if (!/^[a-z][a-z0-9-]{1,28}[a-z0-9]$/.test(address)) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      apiFetch<AddressCheck>('/signup/address', {
        query: { address },
        signal: controller.signal,
        noRetry: true,
      })
        .then(setCheck)
        .catch(() => undefined);
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [address]);
  // Résultat valable seulement pour l'adresse actuellement saisie.
  return check?.address === address ? check : null;
}

function SignupForm({
  platform,
  onCreated,
}: {
  platform: Platform;
  onCreated: (s: Signup) => void;
}) {
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    control,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { clinic_name: '', address: '', email: '', city: '', phone: '' },
  });
  const [addressEdited, setAddressEdited] = useState(false);
  const address = useWatch({ control, name: 'address' });
  const check = useAddressCheck(address);

  const submit = handleSubmit(async (values) => {
    try {
      const created = await apiFetch<Signup>('/signup', {
        method: 'POST',
        body: values,
        noRetry: true,
      });
      onCreated(created);
    } catch (error) {
      const fieldErrors =
        (error instanceof ApiError &&
          (error.problem.errors as Record<string, string[] | string> | undefined)) ||
        {};
      let mapped = false;
      for (const [field, messages] of Object.entries(fieldErrors)) {
        if (field in fields.shape) {
          setError(field as keyof Values, {
            message: Array.isArray(messages) ? messages.join(' ') : String(messages),
          });
          mapped = true;
        }
      }
      if (!mapped) {
        const message =
          error instanceof ApiError && error.code === 'RATE_LIMITED'
            ? 'Trop de demandes depuis cette connexion. Réessayez dans une heure.'
            : errorMessage(error);
        setError('root', { message });
      }
    }
  });

  const addressRegister = register('address', {
    onChange: () => setAddressEdited(true),
  });
  const addressHint = check
    ? check.available
      ? { color: 'teal', text: `Adresse disponible : ${check.domain}` }
      : { color: 'red', text: check.reason }
    : {
        color: 'dimmed',
        text: `Votre adresse : ${address || 'votre-clinique'}.${platform.base_domain}`,
      };

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="sm">
        {errors.root && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} role="alert">
            {errors.root.message}
          </Alert>
        )}
        <Divider label="Votre clinique" labelPosition="left" />
        <TextInput
          label="Nom de la clinique"
          placeholder="Clinique Espoir"
          required
          autoFocus
          error={errors.clinic_name?.message}
          {...register('clinic_name', {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              // Adresse proposée d'après le nom, tant que l'utilisateur ne l'a pas modifiée.
              if (!addressEdited) setValue('address', suggestAddress(event.target.value));
            },
          })}
        />
        <TextInput
          label="Adresse web"
          required
          rightSection={
            <Text size="sm" c="dimmed" pr="xs">
              .{platform.base_domain}
            </Text>
          }
          rightSectionWidth={Math.min(220, 16 + platform.base_domain.length * 8)}
          autoCapitalize="none"
          spellCheck={false}
          error={errors.address?.message}
          description={
            !errors.address && dirtyFields.clinic_name !== undefined ? (
              <Text span size="xs" c={addressHint.color}>
                {addressHint.text}
              </Text>
            ) : undefined
          }
          {...addressRegister}
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput label="Ville" error={errors.city?.message} {...register('city')} />
          <TextInput
            label="Téléphone"
            autoComplete="tel"
            error={errors.phone?.message}
            {...register('phone')}
          />
        </SimpleGrid>
        <TextInput
          label="E-mail de la clinique"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />

        <Divider label="Administrateur de l'espace" labelPosition="left" mt="xs" />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Prénom"
            required
            autoComplete="given-name"
            error={errors.admin_first_name?.message}
            {...register('admin_first_name')}
          />
          <TextInput
            label="Nom"
            required
            autoComplete="family-name"
            error={errors.admin_last_name?.message}
            {...register('admin_last_name')}
          />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Identifiant de connexion"
            required
            autoComplete="username"
            autoCapitalize="none"
            error={errors.admin_username?.message}
            {...register('admin_username')}
          />
          <TextInput
            label="E-mail de l'administrateur"
            required
            autoComplete="email"
            error={errors.admin_email?.message}
            {...register('admin_email')}
          />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <PasswordInput
            label="Mot de passe"
            required
            autoComplete="new-password"
            error={errors.password?.message}
            {...register('password')}
          />
          <PasswordInput
            label="Confirmation"
            required
            autoComplete="new-password"
            error={errors.password_confirm?.message}
            {...register('password_confirm')}
          />
        </SimpleGrid>
        <Text size="xs" c="dimmed">
          10 caractères minimum, pas uniquement des chiffres.
          {platform.mfa &&
            ' À la première connexion, vous activerez la double authentification avec votre téléphone.'}
        </Text>
        <Button type="submit" loading={isSubmitting} size="md" fullWidth mt="xs">
          Créer l&apos;espace de ma clinique
        </Button>
      </Stack>
    </form>
  );
}

function formatDate(value: string | null) {
  return value
    ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(new Date(value))
    : '';
}

function SignupProgress({
  initial,
  onRestart,
  mfa,
}: {
  initial: Signup;
  onRestart: () => void;
  mfa: boolean;
}) {
  const [signup, setSignup] = useState(initial);
  const [pollError, setPollError] = useState<unknown>(null);
  const running = signup.status === 'EN_ATTENTE' || signup.status === 'EN_COURS';

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setTimeout(() => {
      apiFetch<Signup>(`/signup/${signup.id}`, { noRetry: true })
        .then((next) => {
          setPollError(null);
          setSignup(next);
        })
        .catch((error: unknown) => {
          setPollError(error);
          setSignup((s) => ({ ...s })); // relance la minuterie
        });
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [running, signup]);

  if (signup.status === 'ECHEC') {
    return (
      <Stack>
        <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Création impossible">
          {signup.message}
        </Alert>
        <Button variant="light" onClick={onRestart}>
          Recommencer
        </Button>
      </Stack>
    );
  }

  if (signup.status === 'PRETE') {
    const url = clinicUrl(signup.domain);
    return (
      <Stack>
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon color="teal" size={40} radius="xl">
            <IconCircleCheck size={24} />
          </ThemeIcon>
          <div>
            <Text fw={700}>L&apos;espace de {signup.clinic_name} est prêt</Text>
            <Anchor href={url} size="sm">
              {url}
            </Anchor>
          </div>
        </Group>
        <List size="sm" spacing={6}>
          <List.Item>
            Identifiant : <strong>{signup.admin_username}</strong>, avec le mot de passe choisi.
          </List.Item>
          {signup.trial_ends_at && (
            <List.Item>
              Essai gratuit avec tous les modules jusqu&apos;au{' '}
              <strong>{formatDate(signup.trial_ends_at)}</strong>.
            </List.Item>
          )}
          {mfa && (
            <List.Item icon={<IconShieldLock size={16} />}>
              À la première connexion, gardez votre téléphone à portée de main pour activer la
              double authentification.
            </List.Item>
          )}
        </List>
        <Text size="xs" c="dimmed">
          Enregistrez cette adresse dans vos favoris : c&apos;est celle de votre clinique. Un e-mail
          récapitulatif a été envoyé à l&apos;administrateur.
        </Text>
        <Button component="a" href={`${url}/connexion`} size="md" fullWidth>
          Ouvrir mon espace
        </Button>
      </Stack>
    );
  }

  return (
    <Stack>
      <Group gap="sm">
        <Loader size="sm" />
        <Text fw={600}>Création de l&apos;espace de {signup.clinic_name}…</Text>
      </Group>
      <Progress value={100} animated striped aria-label="Création en cours" />
      <Text size="sm" c="dimmed">
        Préparation de la base de données, des services, des référentiels et du compte
        administrateur. Cela prend environ une minute ; ne fermez pas cette page.
      </Text>
      {pollError != null && (
        <Text size="xs" c="orange">
          Connexion instable, nouvel essai… ({errorMessage(pollError)})
        </Text>
      )}
    </Stack>
  );
}

export default function SignupPage({ platform }: { platform: Platform }) {
  const [signup, setSignup] = useState<Signup | null>(null);

  return (
    <AuthShell>
      <div className="login-card wide">
        <Stack>
          <div>
            <h1>Créer l&apos;espace de votre clinique</h1>
            <Text c="dimmed" size="sm" mt={2}>
              {trialText(platform.trial_days)} avec tous les modules, sans engagement. Chaque
              clinique a son adresse et ses données, séparées de celles des autres.
            </Text>
          </div>
          {platform.demo_url && !signup && (
            <Alert color="yellow" variant="light" icon={<IconFlask size={18} />}>
              Envie de voir le logiciel déjà rempli avant de créer votre clinique ?{' '}
              <Anchor href={platform.demo_url} fw={600}>
                Ouvrir la clinique de démonstration
              </Anchor>{' '}
              (données fictives, connexion en un clic).
            </Alert>
          )}
          {!platform.signup_open ? (
            <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
              Les inscriptions sont fermées pour le moment. Contactez l&apos;éditeur du logiciel.
            </Alert>
          ) : signup ? (
            <SignupProgress initial={signup} onRestart={() => setSignup(null)} mfa={platform.mfa} />
          ) : (
            <SignupForm platform={platform} onCreated={setSignup} />
          )}
        </Stack>
      </div>
    </AuthShell>
  );
}
